// src/pages/ProjectDetails/Invoices/form/PaymentForm.jsx

import React, { useState } from 'react';
import { message, Modal, Spin } from 'antd';
import { Formik } from 'formik';
import { DatePicker, Form } from 'formik-antd';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import moment from 'moment';
import errorHandler from '~/Utils/errorHandler';
import { updateAllInvoice } from '~/services/hooks/invoices';
import FormControl from '~/components/Form/FormControl';
import { ModalFooter } from '~/components/Modal';
import Row from '~/components/Row';

/**
 * Props:
 *  - selectedInvoices: array of invoices to be marked as paid
 *  - visible: boolean to open/close modal
 *  - onClose: function to close the modal
 *  - onPaid: function called after all invoices are updated
 */
export default function PaymentForm({ selectedInvoices, visible, onClose, onPaid }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const itemSchema = Yup.object().shape({
    paymentDate: Yup.date().required(),
  });

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const date = moment(values.paymentDate).format('YYYY-MM-DD HH:mm:ss');
      const payload = selectedInvoices.map((invoice) => ({
        ...invoice,
        paymentDate: date,
      }));

      await updateAllInvoice(payload);

      message.success(t('messages:invoices.markedAsPaid')); 

      // Triggers the callback passed by the parent to, for example, reload the list and close the modal
      onPaid();
    } catch (err) {
      errorHandler(err);
    } finally {
      setLoading(false);
    }
  };

  const initialValues = { paymentDate: null };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSave}
      validationSchema={itemSchema}
    >
      {({ errors, isSubmitting, submitForm, resetForm }) => (
        <Modal
          title={t('screens:workbook.receipts.data.insertPaymentDate')}
          width="300px"
          onCancel={() => {
            onClose();
            resetForm();
          }}
          afterClose={resetForm}
          open={visible}
          footer={
            <ModalFooter
              onOk={submitForm}
              loading={loading || isSubmitting}
              onCancel={() => {
                onClose();
                resetForm();
              }}
              cancelColor="default"
            />
          }
        >
          <Spin spinning={loading || isSubmitting}>
            <Form>
              <Row>
                <FormControl
                  field="paymentDate"
                  label={t('screens:workbook.receipts.data.paymentDate')}
                  error={errors.paymentDate}
                >
                  <DatePicker
                    name="paymentDate"
                    format="YYYY-MM-DD 00:00:00"
                    placeholder={t('messages:select')}
                  />
                </FormControl>
              </Row>
            </Form>
          </Spin>
        </Modal>
      )}
    </Formik>
  );
}
